// src/components/Chat.js
// Componente de chat con UI tipo "glass" + exportación (solo PDF).
// - Mantiene tu lógica de fetch a /api/chat y /api/history
// - Añade modal centrado para exportar la conversación (solo "Generar PDF")
// - Endpoint backend esperado:
//     POST /api/export/pdf   -> devuelve application/pdf (descarga)
//
// Asegúrate de tener en Chat.css los estilos del modal (.modal-overlay, .modal-card, etc.)
// y estilos para .user-menu (el menú emergente del avatar).

import { useEffect, useRef, useState } from 'react';
import './Chat.css';

function Chat({ token, onSessionExpired }) {
  // Estado del chat
  const [messages, setMessages] = useState([]); // [{ sender: 'user'|'assistant', text: string }]
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [quickUsed, setQuickUsed] = useState(false); // controla quick actions

  // Estado del modal de exportación
  const [exportOpen, setExportOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');

  // Estado del menú de usuario (avatar)
  const [menuOpen, setMenuOpen] = useState(false);

  // Auto-scroll al final
  const endRef = useRef(null);
  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  // Cargar historial al montar (opcional)
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch('/api/history', {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const mapped = (data?.messages || []).map(m => ({
          sender: m.role === 'user' ? 'user' : 'assistant',
          text: m.text || '',
        }));
        if (mapped.length) setMessages(mapped);
      } catch {
        // Historial es auxiliar: no bloquear UI
      }
    };
    if (token) loadHistory();
  }, [token]);

  // Enviar mensaje
  const handleSend = async () => {
    if (!newMessage.trim() || loading) return;
    const userText = newMessage;

    setMessages(prev => [...prev, { sender: 'user', text: userText }]);
    setNewMessage('');
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: userText }),
      });

      if (!res.ok) {
        // Intenta leer error del backend
        let errMsg = `Error al obtener respuesta (HTTP ${res.status})`;
        try {
          const errJson = await res.json();
          if (errJson?.error) errMsg = errJson.error;
        } catch { /* ignore */ }

        if (res.status === 401 && typeof onSessionExpired === 'function') {
          onSessionExpired();
        }
        throw new Error(errMsg);
      }

      const data = await res.json();
      const aiText = data?.message || '(sin texto)';
      setMessages(prev => [...prev, { sender: 'assistant', text: aiText }]);
    } catch (err) {
      const readable = err?.message || 'Error desconocido';
      setMessages(prev => [...prev, { sender: 'assistant', text: `Error: ${readable}` }]);
      console.error('[CHAT ERROR]', err);
    } finally {
      setLoading(false);
    }
  };

  // Enviar con Enter (sin Shift)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ===== Exportación (solo PDF) =====
  const buildConversationPayload = () =>
    messages.map(m => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

  const handleGeneratePDF = async () => {
    try {
      setExportError('');
      setExportLoading(true);

      const res = await fetch('/api/export/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: 'Conversación AdaIA',
          conversation: buildConversationPayload(),
        }),
      });

      if (!res.ok) {
        let msg = `Fallo al generar PDF (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        if (res.status === 401 && onSessionExpired) onSessionExpired();
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'conversacion-adaia.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setExportOpen(false);
    } catch (err) {
      setExportError(err?.message || 'No se pudo generar el PDF');
    } finally {
      setExportLoading(false);
    }
  };

  // ===== Reiniciar conversación =====
  const handleResetConversation = () => {
    setMessages([]);
    setQuickUsed(false);
    setMenuOpen(false);
  };

  return (
    <div className="chat-glass">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="chat-logo" onClick={() => setMenuOpen(!menuOpen)}>
            <img 
              src="2.svg" 
              alt="Chat Logo" 
              className="chat-logo" 
            />
            {menuOpen && (
              <div className="user-menu">
                <button onClick={handleResetConversation}>
                  Reiniciar conversación
                </button>
                <button
                  onClick={() => {
                    if (typeof onSessionExpired === 'function') {
                      onSessionExpired();
                    }
                    setMenuOpen(false);
                  }}
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
          <div>
            <h3>AdaIA</h3>
            <p>Tu compañera emocional</p>
          </div>
        </div>
        <div className="chat-header-controls">
          <button
            title="Exportar"
            onClick={() => {
              setExportError('');
              setExportOpen(true);
            }}
          >
            ⬇️
          </button>
        </div>
      </div>

      {/* Mensajes */}
      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`bubble-row ${msg.sender}`}>
            <div className="avatar">{msg.sender === 'user' ? '🧑' : '🤖'}</div>
            <div className="bubble">
              <p>{msg.text}</p>
            </div>
          </div>
        ))}

        {messages.length === 0 && !loading && (
          <p className="welcome">👋 Hola, escribe un mensaje para comenzar.</p>
        )}
        {loading && <p className="thinking"><em>AdaIA está pensando...</em></p>}

        <div ref={endRef} />
      </div>

      {/* Acciones rápidas */}
      <div className="chat-quick">
        {!quickUsed && messages.length === 0 && (
          <button
            onClick={() => {
              setNewMessage('Quiero comenzar una conversación');
              setQuickUsed(true);
            }}
          >
            Iniciar conversación
          </button>
        )}

        {!quickUsed && messages.length > 0 && (
          <>
            <button
              onClick={() => {
                setNewMessage('Me siento abrumado/a');
                setQuickUsed(true);
              }}
            >
              Me siento abrumado/a
            </button>
            <button
              onClick={() => {
                setNewMessage('Necesito hablar sobre mi ansiedad');
                setQuickUsed(true);
              }}
            >
              Hablar sobre ansiedad
            </button>
          </>
        )}
      </div>

      {/* Input */}
      <div className="chat-input">
        <textarea
          rows={1}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu mensaje aquí..."
          disabled={loading}
        />
        <button onClick={handleSend} disabled={loading}>➤</button>
      </div>

      {/* Modal de exportación (solo PDF) */}
      {exportOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="export-title">
          <div className="modal-card">
            <h4 id="export-title">Exportar conversación</h4>
            <p className="modal-subtitle">Se descargará un PDF con tu conversación.</p>

            {exportError && <p className="modal-error">{exportError}</p>}

            <div className="modal-actions">
              <button className="btn-primary" onClick={handleGeneratePDF} disabled={exportLoading}>
                {exportLoading ? 'Generando…' : 'Generar PDF'}
              </button>
            </div>

            <button className="modal-close" onClick={() => setExportOpen(false)} aria-label="Cerrar">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
